import { useEffect, useRef, useState } from 'react'
import {
  AXIS_KIND_LABEL,
  combinationCount,
  parseOptions,
  type AxisDraft,
  type AxisKind,
} from '../../lib/shop'

/**
 * What an item varies by, and the options for each — David's "what varies and
 * then the options for those variations", generalised (068).
 *
 * ── ONE EDITOR, TWO CALLERS ─────────────────────────────────────────────────
 *
 * The create form and the row editor edit the same shape and send it to the
 * same function, so they share this. Two editors would be two places for the
 * "an axis needs at least one option" rule to live, and the second one is where
 * it gets forgotten.
 *
 * ── OPTIONS ARE PILLS, ADDED ON ENTER (David, 14 Sep) ───────────────────────
 *
 * They were a comma-separated text box. Three things were wrong with that and
 * none of them is taste:
 *
 *   1. **There is no feedback until you look away.** Typing "Navy, Grey" gives
 *      no sign that it is two things rather than one until the count beneath
 *      updates, and a stray comma or a missing one is invisible.
 *   2. **Removing the middle one means editing a sentence.** Deleting "Grey"
 *      from "Navy, Grey, Black" is a text edit that leaves a double comma if
 *      you are careless.
 *   3. **It disagrees with everything else in this app.** A set of discrete
 *      values already renders as chips on the feed, the wall, the admin
 *      filters. This was the one place it was a sentence.
 *
 * So: type, press Enter, get a pill. A comma also commits, because people paste
 * "S, M, L" and should not have to think about it; a paste of a whole list is
 * split on commas AND newlines (a column out of a spreadsheet). Backspace on an
 * empty box removes the last pill, which is the convention everywhere else this
 * control exists.
 *
 * ── THE AXIS NAME IS TEXT WITH A PENCIL, NOT A PERMANENT BOX ────────────────
 *
 * David: "once that name is created it should not be in a text box every time".
 * An input is an invitation to type, and a row of them makes the name look like
 * the thing being edited when the OPTIONS are. A named axis renders as a
 * heading; the pencil opens it. A custom axis with no name yet opens in the box
 * — there is nothing to read, and the point of it is that you must name it.
 *
 * ── FOUR KINDS, THREE OF THEM NAMED ─────────────────────────────────────────
 *
 * Size, colour and fit are standard: the database knows what they mean, so
 * sizes order S–M–L rather than alphabetically and a report can find every
 * size in the catalogue. "Something else" is `custom` and there may be any
 * number of them — which is the thing 067 capped and 068 removed.
 *
 * **`fit` IS A GARMENT CUT.** The offered options are Men's / Women's because
 * that is what the shirt is, not what the person is; anybody may pick either
 * and nothing about them is recorded. If somebody retypes these as Male and
 * Female the schema stops describing a garment and starts describing an
 * employee, which is the one thing this axis exists to avoid.
 *
 * ── PICTURES ARE NOT HERE, AND THAT IS NOT AN OVERSIGHT ─────────────────────
 *
 * An option's image (070) hangs off a SAVED option's id, and this editor works
 * on a draft that has labels and no ids — the options do not exist until the
 * catalogue is written. So images live on the saved item's own panel. Putting
 * an upload control here would mean inventing an id, or holding a file against
 * a label that is still being typed.
 */

/**
 * Exported so the suite can assert on the SUGGESTION rather than on a rendered
 * placeholder. Since options became pills the fit suggestion is a pre-fill that
 * happens when the Fit chip is pressed, and a static render of a saved item
 * cannot show it — so the thing to test is this list, which is what decides it.
 */
export const ADDABLE: { kind: AxisKind; label: string; placeholder: string; suggest?: string }[] = [
  { kind: 'colour', label: 'Colour', placeholder: 'Navy' },
  { kind: 'size', label: 'Size', placeholder: 'S' },
  // The suggestion is the garment, never the person — see the header.
  { kind: 'fit', label: 'Fit', placeholder: "Men's", suggest: "Men's, Women's" },
  { kind: 'custom', label: 'Something else', placeholder: 'Short' },
]

export function AxisEditor({
  axes,
  onChange,
  disabled,
}: {
  axes: AxisDraft[]
  onChange: (next: AxisDraft[]) => void
  disabled?: boolean
}) {
  const used = new Set(axes.map((a) => a.kind))
  const total = combinationCount(axes)
  const incomplete = axes.filter((a) => a.options.length === 0)

  function update(i: number, patch: Partial<AxisDraft>) {
    onChange(axes.map((a, n) => (n === i ? { ...a, ...patch } : a)))
  }

  return (
    <div className="axes">
      {axes.length === 0 && (
        <p className="admin__hint">
          Nothing yet, so this item comes one way and has a single stock line —
          which is right for a mug. Add an axis if it comes in more than one.
        </p>
      )}

      <ul className="axes__list">
        {axes.map((axis, i) => {
          const spec = ADDABLE.find((s) => s.kind === axis.kind)
          return (
            <li key={`${axis.kind}-${i}`} className="axes__row">
              <div className="axes__head">
                <AxisName
                  axis={axis}
                  disabled={disabled}
                  onRename={(label) => update(i, { label })}
                />
                <button
                  type="button"
                  className="btn btn--xs"
                  disabled={disabled}
                  onClick={() => onChange(axes.filter((_, n) => n !== i))}
                >
                  Remove
                </button>
              </div>

              <OptionPills
                options={axis.options}
                disabled={disabled}
                placeholder={spec?.placeholder ?? 'One'}
                label={axis.label || spec?.label || 'this'}
                onChange={(options) => update(i, { options })}
              />

              {axis.options.length === 0 ? (
                <p className="shopitems__refusal" role="alert">
                  {axis.label || spec?.label || 'This'} needs at least one option.
                </p>
              ) : (
                <p className="admin__hint">
                  {axis.options.length} {axis.options.length === 1 ? 'option' : 'options'}
                  {axis.kind === 'size' && ' · sizes are ordered S, M, L rather than alphabetically'}
                  {axis.kind === 'fit' && ' · this describes the garment, not the person wearing it'}
                </p>
              )}
            </li>
          )
        })}
      </ul>

      <div className="axes__add">
        <span className="axes__addlabel">Add:</span>
        {ADDABLE.map((s) => (
          <button
            key={s.kind}
            type="button"
            className="chip"
            // One axis of each standard kind, which is the database rule too —
            // an item cannot come in two different sizes-of-size. Any number of
            // custom ones.
            disabled={disabled || (s.kind !== 'custom' && used.has(s.kind))}
            onClick={() =>
              onChange([
                ...axes,
                {
                  kind: s.kind,
                  label: s.kind === 'custom' ? '' : AXIS_KIND_LABEL[s.kind],
                  options: s.suggest ? parseOptions(s.suggest) : [],
                },
              ])
            }
          >
            {s.label}
          </button>
        ))}
      </div>

      {axes.length > 0 && incomplete.length === 0 && (
        <p className="admin__hint">
          {axes.map((a) => a.label || 'unnamed').join(' × ')} makes{' '}
          <strong>
            {total} {total === 1 ? 'combination' : 'combinations'}
          </strong>
          , each with its own stock count in each room.
        </p>
      )}
    </div>
  )
}

/**
 * The axis name: a heading with a pencil, or a box while it is being typed.
 *
 * An unnamed custom axis opens in the box — there is nothing to read yet, and
 * the whole point of a custom axis is that it needs naming. A standard one
 * arrives already called Colour, so it reads as a heading until somebody wants
 * it called something else.
 *
 * Committing on Enter AND on blur, because both are things people do; Escape
 * abandons, which is the reason the draft is held locally rather than written
 * through on every keystroke.
 */
function AxisName({
  axis,
  disabled,
  onRename,
}: {
  axis: AxisDraft
  disabled?: boolean
  onRename: (label: string) => void
}) {
  const [editing, setEditing] = useState(axis.label.trim() === '')
  const [draft, setDraft] = useState(axis.label)
  const box = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) box.current?.focus()
  }, [editing])

  function commit() {
    const next = draft.trim()
    // An empty name is not a rename — a standard axis keeps its kind label and
    // a custom one stays in the box, because it has nothing else to be called.
    if (next === '') {
      if (axis.kind === 'custom') return
      setDraft(axis.label)
      setEditing(false)
      return
    }
    onRename(next)
    setEditing(false)
  }

  if (editing) {
    return (
      <label className="admin__field">
        <span>{axis.kind === 'custom' ? 'What varies' : 'Called'}</span>
        <input
          ref={box}
          value={draft}
          disabled={disabled}
          placeholder={axis.kind === 'custom' ? 'Sleeve' : AXIS_KIND_LABEL[axis.kind]}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // The editor sits inside a form; Enter here means "I have named
              // it", never "save the item".
              e.preventDefault()
              commit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setDraft(axis.label)
              if (axis.label.trim() !== '') setEditing(false)
            }
          }}
        />
      </label>
    )
  }

  return (
    <p className="axes__name">
      <span>{axis.label}</span>
      <button
        type="button"
        className="btn btn--xs btn--quiet"
        disabled={disabled}
        aria-label={`Rename ${axis.label}`}
        onClick={() => {
          setDraft(axis.label)
          setEditing(true)
        }}
      >
        ✎
      </button>
    </p>
  )
}

/**
 * Options as pills. Type, Enter, pill.
 *
 * `commit` is deliberately the ONE way a value gets in, whether it arrived by
 * Enter, by a comma, by a paste or by leaving the box — so the trimming and the
 * duplicate check cannot be right on one path and missing on another. It reuses
 * `parseOptions`, which is the same splitting the server does.
 */
export function OptionPills({
  options,
  onChange,
  disabled,
  placeholder,
  label,
}: {
  options: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  placeholder: string
  label: string
}) {
  const [draft, setDraft] = useState('')

  function commit(raw: string) {
    const parsed = parseOptions(raw)
    if (parsed.length === 0) {
      setDraft('')
      return
    }
    const seen = new Set(options.map((o) => o.toLowerCase()))
    const added = parsed.filter((p) => !seen.has(p.toLowerCase()))
    if (added.length > 0) onChange([...options, ...added])
    setDraft('')
  }

  return (
    <div className="axes__options">
      <span className="axes__addlabel" id={`opt-${label}`}>Options</span>
      <div className="chips" role="group" aria-labelledby={`opt-${label}`}>
        {options.map((o, i) => (
          <span key={`${o}-${i}`} className="chip chip--value">
            {o}
            <button
              type="button"
              className="chip__x"
              disabled={disabled}
              aria-label={`Remove ${o} from ${label}`}
              onClick={() => onChange(options.filter((_, n) => n !== i))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        className="axes__entry"
        value={draft}
        disabled={disabled}
        placeholder={options.length === 0 ? placeholder : 'Add another…'}
        aria-label={`Add an option to ${label}`}
        onChange={(e) => {
          // A comma commits as you type, so pasting "S, M, L" lands as three
          // pills rather than one pill called "S, M, L".
          if (/[,\n]/.test(e.target.value)) commit(e.target.value)
          else setDraft(e.target.value)
        }}
        // Leaving the box with something half-typed keeps it rather than
        // discarding it — losing what somebody wrote is worse than an extra
        // pill they can remove.
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit(draft)
          } else if (e.key === 'Backspace' && draft === '' && options.length > 0) {
            // The convention everywhere this control exists. Only on an EMPTY
            // box, so it never eats a pill while somebody is mid-word.
            e.preventDefault()
            onChange(options.slice(0, -1))
          }
        }}
      />
    </div>
  )
}

/** Whether this shape can be saved: every axis named and carrying options. */
export function axesReady(axes: AxisDraft[]): boolean {
  return axes.every((a) => a.label.trim() !== '' && a.options.length > 0)
}
